<div class="analytics-layout">
  <div class="title-bar title-bar--responsive">
    <div class="title-bar__heading">
      <h1>ANALYTICS</h1>
    </div>
    <div class="title-bar__actions">
      <span class="analytics-export-note">Exports records after 3/1/2026</span>
      <button mat-flat-button type="button" (click)="exportCsv()" [disabled]="isExporting">
        {{ isExporting ? 'Exporting...' : 'Export CSV' }}
      </button>
    </div>
  </div>

  @if (errorMessage) {
    <div class="analytics-message analytics-message--error" role="alert">{{ errorMessage }}</div>
  }

  @if (isLoading) {
    <div class="analytics-message analytics-message--soft">Loading activity...</div>
  } @else {
    <section class="analytics-summary" aria-labelledby="analytics-summary-title">
      <h2 id="analytics-summary-title" class="analytics-section-title">School admin activity</h2>
      <p class="analytics-section-copy">
        @if (recentRecords.length === 0) {
          No page-view records found for this school.
        } @else if (sampleCapped && sampleOldest) {
          From the latest {{ recentRecords.length | number }} page views, back to {{ sampleOldest | date: 'mediumDate' }}.
        } @else if (sampleOldest) {
          From {{ recentRecords.length | number }} page views since {{ sampleOldest | date: 'mediumDate' }}.
        }
      </p>

      <div class="analytics-stats">
        <div class="analytics-stat">
          <p class="analytics-stat__label">Last school visit</p>
          @if (summary.lastSchoolVisit; as visit) {
            <p class="analytics-stat__value">{{ visit.at.toISOString() | dateAgo }}</p>
            <p class="analytics-stat__meta">{{ visit.email }} on {{ visit.screen }}</p>
          } @else {
            <p class="analytics-stat__value">None</p>
            <p class="analytics-stat__meta">No assigned school users in this sample</p>
          }
        </div>
        <div class="analytics-stat">
          <p class="analytics-stat__label">School users</p>
          <p class="analytics-stat__value">{{ summary.schoolUserCount | number }}</p>
          <p class="analytics-stat__meta">Distinct assigned users who opened a screen</p>
        </div>
        <div class="analytics-stat">
          <p class="analytics-stat__label">Most used screen</p>
          @if (summary.topScreen; as top) {
            <p class="analytics-stat__value">{{ top.screen }}</p>
            <p class="analytics-stat__meta">{{ top.count | number }} school-staff page views</p>
          } @else {
            <p class="analytics-stat__value">—</p>
            <p class="analytics-stat__meta">Not enough school-staff activity</p>
          }
        </div>
      </div>
    </section>

    @if (summary.screenCounts.length > 0) {
      <section class="analytics-screens" aria-labelledby="analytics-screens-title">
        <h2 id="analytics-screens-title" class="analytics-section-title">Screens used by school staff</h2>
        <div
          class="analytics-spark"
          role="img"
          [attr.aria-label]="'School staff page views by screen in this sample'"
        >
          @for (bar of summary.screenCounts; track bar.path) {
            <div class="analytics-spark__col">
              <span class="analytics-spark__count">{{ bar.count | number }}</span>
              <div
                class="analytics-spark__bar"
                [style.height.%]="bar.heightPct"
                [attr.title]="bar.screen + ': ' + bar.count"
              ></div>
              <span class="analytics-spark__label">{{ bar.screen }}</span>
            </div>
          }
        </div>
      </section>
    }

    <mat-card appearance="outlined">
      <mat-card-header>
        <mat-card-title>
          <h2>Recent activity</h2>
        </mat-card-title>
        <mat-card-subtitle class="analytics-subtitle">
          Consecutive visits to the same screen are grouped. Assigned school users are listed separately from Rewire and College Board accounts.
        </mat-card-subtitle>
      </mat-card-header>
      <mat-card-content class="analytics-content">
        <div class="analytics-filter" role="group" aria-label="Activity audience">
          <button
            type="button"
            class="analytics-filter__btn"
            [class.analytics-filter__btn--active]="audience === 'school'"
            [attr.aria-pressed]="audience === 'school'"
            (click)="setAudience('school')"
          >
            School staff
          </button>
          <button
            type="button"
            class="analytics-filter__btn"
            [class.analytics-filter__btn--active]="audience === 'support'"
            [attr.aria-pressed]="audience === 'support'"
            (click)="setAudience('support')"
          >
            Support
          </button>
          <button
            type="button"
            class="analytics-filter__btn"
            [class.analytics-filter__btn--active]="audience === 'all'"
            [attr.aria-pressed]="audience === 'all'"
            (click)="setAudience('all')"
          >
            All
          </button>
        </div>

        @if (visibleActivity.length === 0) {
          <div class="analytics-message analytics-message--soft">
            @if (audience === 'school') {
              No school-staff visits in this sample. Try Support or All.
            } @else {
              No matching activity in this sample.
            }
          </div>
        } @else {
          <div class="analytics-table-wrapper">
            <table class="analytics-table">
              <thead>
                <tr>
                  <th scope="col" id="analytics-when">When</th>
                  <th scope="col" id="analytics-who">Who</th>
                  <th scope="col" id="analytics-screen">Screen</th>
                  <th scope="col" id="analytics-views">Views</th>
                </tr>
              </thead>
              <tbody>
                @for (row of visibleActivity; track row.email + row.path + (row.latest?.toISOString() ?? '') + row.count) {
                  <tr>
                    <th scope="row" [attr.id]="activityRowId($index)">
                      @if (row.latest) {
                        {{ row.latest | date: 'medium' }}
                      } @else {
                        Unknown time
                      }
                    </th>
                    <td>
                      {{ row.email }}
                      <span class="analytics-who-tag">{{ row.schoolStaff ? 'School' : 'Support' }}</span>
                    </td>
                    <td>{{ row.screen }}</td>
                    <td>
                      {{ row.count | number }}
                      @if (row.count > 1 && row.earliest && row.latest) {
                        <span class="analytics-views-range">since {{ row.earliest | date: 'shortTime' }}</span>
                      }
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
      </mat-card-content>
    </mat-card>
  }
</div>
